import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Check, X, Lock } from 'lucide-react';
import { Container } from '../ui/Container/Container';
import { useScrollReveal } from './useScrollReveal';
import { useAuthStore } from '../../stores/authStore';
import { BillingService } from '../../services/BillingService';
import type { BillingPlanDto } from '../../services/BillingService';

const IS_ALPHA = import.meta.env.VITE_IS_ALPHA === 'true';

/* ── Static credit examples ───────────────────────────── */
const EXAMPLES = [
  { label: 'Up to 4,000 images with budget models', cost: '~1 cr' },
  { label: '~1,333 images with GPT Image 1.5', cost: '3 cr' },
  { label: '~400 images with Seedream V4', cost: '10 cr' },
  { label: '~114 short video clips', cost: '35 cr' },
  { label: 'Unlimited text generation', cost: '0 cr' },
];

/* ── Fallback features if API fails ───────────────────── */
const FALLBACK_FEATURES: { text: string; tooltipHtml?: string | null; isIncluded: boolean }[] = [
  { text: '4,000 credits every month', isIncluded: true },
  { text: 'All current image models', isIncluded: true },
  { text: 'All current video models', isIncluded: true },
  { text: 'Unlimited AI text generation', isIncluded: true },
  { text: 'Unlimited canvases & projects', isIncluded: true },
  { text: 'Image & video downloads', isIncluded: true },
  { text: 'Credit top-ups (Coming soon)', isIncluded: true },
  { text: 'Team collaboration (5 seats)', isIncluded: true },
];

/* ── Helpers ──────────────────────────────────────────── */
function fmtPrice(cents: number): string {
  const d = cents / 100;
  return d % 1 === 0 ? `$${d}` : `$${d.toFixed(2)}`;
}

/* ── Feature Row with tooltip underline ──────────────── */
function FeatureRow({
  text,
  tooltipHtml,
  included,
  dark,
  muted,
}: {
  text: string;
  tooltipHtml?: string | null;
  included: boolean;
  dark?: boolean;
  muted?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<'above' | 'below'>('above');
  const rowRef = useRef<HTMLDivElement>(null);
  const timer = useRef<number | null>(null);
  const hasTip = !!tooltipHtml;

  const enter = () => {
    if (!hasTip) return;
    if (rowRef.current) {
      setPos(rowRef.current.getBoundingClientRect().top > 300 ? 'above' : 'below');
    }
    timer.current = window.setTimeout(() => setOpen(true), 180);
  };
  const leave = () => {
    if (timer.current) clearTimeout(timer.current);
    setOpen(false);
  };

  let checkColor: string;
  let textColor: string;
  /* Dotted underline color for tooltip-enabled features */
  let underlineColor: string;
  if (!included) {
    checkColor = dark ? 'text-[#3e4452]' : 'text-[#d4d8e0]';
    textColor = dark ? 'text-[#3e4452]' : 'text-[#bec3cd]';
    underlineColor = 'transparent';
  } else if (dark) {
    checkColor = 'text-[#c8d45a]';
    textColor = muted ? 'text-[#7a8294]' : 'text-[#c9cdd6]';
    underlineColor = 'rgba(201,205,214,0.25)';
  } else {
    checkColor = muted ? 'text-[#c0c7d0]' : 'text-[#8b9656]';
    textColor = muted ? 'text-[#6b7280]' : 'text-[#3a4252]';
    underlineColor = 'rgba(0,0,0,0.12)';
  }

  return (
    <div
      ref={rowRef}
      className={`flex items-center gap-2.5 py-[5px] text-[0.8rem] leading-snug relative ${hasTip ? 'cursor-help' : ''}`}
      onMouseEnter={enter}
      onMouseLeave={leave}
    >
      {included ? (
        <Check className={`w-[14px] h-[14px] shrink-0 ${checkColor}`} />
      ) : (
        <X className={`w-[14px] h-[14px] shrink-0 ${checkColor}`} />
      )}
      <span
        className={`${textColor} ${!included ? 'line-through' : ''}`}
        style={hasTip && included ? { borderBottom: `1px dotted ${underlineColor}`, paddingBottom: 1 } : undefined}
      >
        {text}
      </span>

      {hasTip && open && (
        <div
          className={`absolute left-0 z-50 w-[270px] px-3.5 py-3 rounded-[10px] text-[0.72rem] leading-relaxed pointer-events-none ${
            pos === 'above' ? 'bottom-full mb-2' : 'top-full mt-2'
          }`}
          style={{
            background: dark ? '#2a3040' : '#1a2233',
            color: 'rgba(255,255,255,0.78)',
            animation: 'lpTipIn 0.12s ease-out',
            boxShadow: '0 12px 40px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.06)',
          }}
        >
          <div dangerouslySetInnerHTML={{ __html: tooltipHtml! }} />
          <div
            className={`absolute left-5 w-[7px] h-[7px] rotate-45 ${
              pos === 'above' ? 'bottom-[-4px]' : 'top-[-4px]'
            }`}
            style={{ background: dark ? '#2a3040' : '#1a2233' }}
          />
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
 *  LandingPricing
 * ═══════════════════════════════════════════════════════ */
export function LandingPricing() {
  const ref = useScrollReveal<HTMLDivElement>();
  const user = useAuthStore((s) => s.user);
  const [plans, setPlans] = useState<BillingPlanDto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    BillingService.listPublicPlans()
      .then((data) => {
        setPlans(
          data.filter((p) => p.isActive && p.key !== 'alpha')
            .sort((a, b) => a.displayOrder - b.displayOrder),
        );
      })
      .catch((err) => console.error('Failed to load plans:', err))
      .finally(() => setLoading(false));
  }, []);

  const monthly = plans.find((p) => p.key === 'monthly' || p.key === 'plan_monthly');
  const yearly = plans.find((p) => p.key === 'yearly' || p.key === 'plan_yearly');

  const mPrice = monthly?.prices.find((p) => p.interval === 'month');
  const yPrice = yearly?.prices.find((p) => p.interval === 'year');
  const yMonthly = yPrice ? Math.round((yPrice.unitAmount / 12) * 100) / 100 : null;
  const savings = mPrice && yPrice ? (mPrice.unitAmount * 12 - yPrice.unitAmount) / 100 : null;

  const ctaPath = user ? '/organization/credits' : '/auth';

  // Payment Link URLs from the plans API (stored in billing_prices.payment_link_url)
  const monthlyPaymentLink = mPrice?.paymentLinkUrl || '';
  const yearlyPaymentLink = yPrice?.paymentLinkUrl || '';

  const getFeatures = (plan: BillingPlanDto | undefined) =>
    plan?.features?.length
      ? [...plan.features].sort((a, b) => a.displayOrder - b.displayOrder)
      : FALLBACK_FEATURES.map((f, i) => ({ ...f, id: String(i), displayOrder: i, category: null }));

  return (
    /* FIX 6: bg-white to alternate with gray collab above and gray FAQ below */
    <section className="py-12 md:py-20 bg-white" id="pricing">
      <Container size="lg" className="max-w-[1060px]">
        {/* ── Header ── */}
        <div ref={ref} className="lp-reveal text-center mb-10 md:mb-12">
          <p className="font-mono text-[0.65rem] uppercase tracking-[0.18em] text-[#a8b44a] mb-3 font-semibold">
            Pricing
          </p>
          <h2
            className="text-[clamp(1.8rem,4.5vw,2.75rem)] font-bold leading-[1.1] tracking-[-0.02em] mb-4"
            style={{ color: '#111827' }}
          >
            One plan. Every model.
          </h2>
          <p className="text-[clamp(0.95rem,2vw,1.08rem)] text-[#6b7280] max-w-[480px] mx-auto leading-relaxed">
            No per-model subscriptions. No surprise fees.
            <br className="hidden sm:block" />
            One plan covers image, video, and text.
          </p>

          {/* Free trial pill — alpha only */}
          {IS_ALPHA && (
            <Link
              to="/auth?tab=register"
              className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 rounded-full text-[0.78rem] font-medium no-underline transition-all hover:shadow-sm"
              style={{ background: 'rgba(200,212,90,0.13)', color: '#5c6b20' }}
            >
              7-day free trial — try every model
              <span className="opacity-50 ml-0.5">→</span>
            </Link>
          )}
        </div>

        {/* ── Cards ── */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-[#c8d45a] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="max-w-[820px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 items-start">

            {/* ─── Monthly Card ─── */}
            <div
              className="lp-pricing-card rounded-2xl p-6 md:p-7 md:mt-8 transition-shadow duration-300"
              style={{
                background: '#fafbfc',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
              }}
            >
              <p className="text-[0.65rem] font-bold uppercase tracking-[0.12em] text-[#9ca3af] mb-4">Monthly</p>

              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-[2.6rem] font-bold tracking-tight text-[#1f2937] leading-none">
                  {mPrice ? fmtPrice(mPrice.unitAmount) : '$19'}
                </span>
                <span className="text-[0.85rem] text-[#9ca3af] font-medium">/mo</span>
              </div>
              <p className="text-[0.75rem] text-[#9ca3af] mb-5">Billed monthly. Cancel anytime.</p>

              {user ? (
                <Link
                  to="/organization/credits"
                  className="w-full flex items-center justify-center py-[11px] rounded-xl text-[0.82rem] font-semibold no-underline transition-all duration-200 hover:border-[#9ca3af] hover:shadow-sm"
                  style={{ border: '1.5px solid #d1d5db', color: '#374151', background: '#fff' }}
                >
                  Get Monthly
                </Link>
              ) : (
                <a
                  href={monthlyPaymentLink}
                  className="w-full flex items-center justify-center py-[11px] rounded-xl text-[0.82rem] font-semibold no-underline transition-all duration-200 hover:border-[#9ca3af] hover:shadow-sm"
                  style={{ border: '1.5px solid #d1d5db', color: '#374151', background: '#fff' }}
                >
                  Get Monthly
                </a>
              )}

              <div className="mt-5 pt-4 border-t border-[#f0f1f3]">
                {getFeatures(monthly).filter((f) => f.isIncluded).map((f) => (
                  <FeatureRow
                    key={f.id ?? f.text}
                    text={f.text}
                    tooltipHtml={(f as any).tooltipHtml}
                    included
                    muted
                  />
                ))}
              </div>
            </div>

            {/* ─── Yearly Card (Hero) ─── */}
            <div className="relative">
              <div
                className="lp-pricing-card-hero rounded-2xl p-6 md:p-7 relative overflow-hidden transition-shadow duration-300"
                style={{
                  background: 'linear-gradient(170deg, #151d2e 0%, #111827 30%, #0f1420 100%)',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.07), 0 25px 60px rgba(0,0,0,0.18)',
                }}
              >
                {/* Subtle glow accents */}
                <div
                  className="absolute -top-16 -right-16 w-[180px] h-[180px] rounded-full pointer-events-none"
                  style={{ background: 'radial-gradient(circle, rgba(200,212,90,0.08) 0%, transparent 70%)' }}
                />
                <div
                  className="absolute -bottom-24 -left-24 w-[200px] h-[200px] rounded-full pointer-events-none"
                  style={{ background: 'radial-gradient(circle, rgba(200,212,90,0.04) 0%, transparent 70%)' }}
                />

                <div className="relative z-[1]">
                  {/* FIX 3: Better YEARLY + SAVE pill sizing */}
                  <div className="flex items-center gap-2.5 mb-4 mt-2">
                    <p className="text-[0.7rem] font-bold uppercase tracking-[0.1em] text-[#c8d45a]">Yearly</p>
                    {savings != null && (
                      <span
                        className="text-[0.65rem] font-bold uppercase tracking-[0.04em] px-2.5 py-[3px] rounded-md"
                        style={{ background: 'rgba(200,212,90,0.12)', color: '#c8d45a' }}
                      >
                        Save ${savings}
                      </span>
                    )}
                  </div>

                  <div className="flex items-baseline gap-2 mb-1">
                    <span
                      className="text-[1.05rem] line-through font-medium"
                      style={{ color: 'rgba(239,68,68,0.55)' }}
                    >
                      {mPrice ? fmtPrice(mPrice.unitAmount) : '$19'}
                    </span>
                    <span className="text-[2.8rem] font-bold tracking-tight text-white leading-none">
                      {yMonthly != null ? fmtPrice(yMonthly) : '$15.83'}
                    </span>
                    <span className="text-[0.85rem] text-[#6b7280] font-medium">/mo</span>
                  </div>
                  <p className="text-[0.75rem] text-[#6b7280] mb-5">
                    Billed at {yPrice ? fmtPrice(yPrice.unitAmount) : '$190'}/year
                  </p>

                  {user ? (
                    <Link
                      to="/organization/credits"
                      className="w-full flex items-center justify-center py-3 rounded-xl text-[0.85rem] font-bold no-underline transition-all duration-200 hover:brightness-110 hover:shadow-lg"
                      style={{
                        background: 'linear-gradient(135deg, #d0dc5e 0%, #b8c442 100%)',
                        color: '#111807',
                        boxShadow: '0 2px 12px rgba(200,212,90,0.35), inset 0 1px 0 rgba(255,255,255,0.25)',
                      }}
                    >
                      Get Annual →
                    </Link>
                  ) : (
                    <a
                      href={yearlyPaymentLink}
                      className="w-full flex items-center justify-center py-3 rounded-xl text-[0.85rem] font-bold no-underline transition-all duration-200 hover:brightness-110 hover:shadow-lg"
                      style={{
                        background: 'linear-gradient(135deg, #d0dc5e 0%, #b8c442 100%)',
                        color: '#111807',
                        boxShadow: '0 2px 12px rgba(200,212,90,0.35), inset 0 1px 0 rgba(255,255,255,0.25)',
                      }}
                    >
                      Get Annual →
                    </a>
                  )}

                  {/* FIX 4: Features with tooltip underlines */}
                  <div className="mt-5 pt-4 border-t border-white/[0.07]">
                    {getFeatures(yearly).map((f) => (
                      <FeatureRow
                        key={f.id ?? f.text}
                        text={f.text}
                        tooltipHtml={(f as any).tooltipHtml}
                        included={f.isIncluded}
                        dark
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Trust + credit examples ── */}
        <div className="max-w-[460px] mx-auto mt-10 text-center">
          <p className="text-[0.68rem] text-[#9ca3af] flex items-center justify-center gap-1.5">
            <Lock className="w-3 h-3" />
            Secure checkout via Stripe · Cancel anytime
          </p>
        </div>

        <div
          className="max-w-[460px] mx-auto mt-6 rounded-xl p-5"
          style={{
            background: '#f9fafb',
            border: '1px solid #e5e7eb',
          }}
        >
          <p className="font-mono text-[0.58rem] text-[#9ca3af] uppercase tracking-[0.12em] mb-3 font-semibold">
            What 4,000 credits gets you
          </p>
          <div className="space-y-0">
            {EXAMPLES.map((e) => (
              <div key={e.label} className="flex justify-between items-center text-[0.78rem] py-[4px]">
                <span className="text-[#4b5563]">{e.label}</span>
                <span className="text-[#9ca3af] font-mono text-[0.7rem] tabular-nums">{e.cost}</span>
              </div>
            ))}
          </div>
        </div>
      </Container>

      <style>{`
        @keyframes lpTipIn {
          from { opacity: 0; transform: translateY(3px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .lp-pricing-card:hover {
          box-shadow: 0 4px 20px rgba(0,0,0,0.06) !important;
        }
        .lp-pricing-card-hero:hover {
          box-shadow: 0 4px 6px rgba(0,0,0,0.07), 0 30px 70px rgba(0,0,0,0.22) !important;
        }
      `}</style>
    </section>
  );
}
